import type { PathParams } from './path-params';
import type { KnownRoute } from './routes';

/**
 * Substitutes the `:name` segments of a route pattern.
 *
 * Values are looked up by a key parsed out of the pattern at runtime, so the
 * read can miss even when the caller's `PathParams` says every param is
 * present — a params object built from data, or a pattern that gained a
 * segment. That miss is what the throw reports, and it is why this takes the
 * open record rather than `PathParams<Path>`.
 *
 * @public
 */
export function substitutePathParams(
  path: string,
  params: Readonly<Record<string, string | number | undefined>>,
): string {
  return path.replace(/:([^/]+)/g, (_, key: string) => {
    const value = params[key];
    if (value == null) {
      throw new Error(`Missing ":${key}" param`);
    }

    return String(value);
  });
}

/**
 * @deprecated Prefer `router.buildLocation(...)` for Hydra-owned pages. Retained for
 * embedded/manual routes in `ROUTES`, and for building embedded-app hrefs to
 * tree-registered paths that carry embedded-only query params the TanStack route does
 * not declare (e.g. the integrations directory's `selectedCategory`/`selectedType`).
 */
export function generatePath<Path extends KnownRoute>(
  path: Path,
  params: PathParams<Path>,
  query?: Record<string, string | number | boolean | undefined | null>,
  hash?: string,
): string {
  const base = substitutePathParams(path, params);

  const searchParams = new URLSearchParams();
  if (query != null) {
    for (const [key, value] of Object.entries(query)) {
      if (value != null) {
        searchParams.set(key, String(value));
      }
    }
  }

  const search = searchParams.toString();
  const withSearch = search.length > 0 ? `${base}?${search}` : base;
  return hash != null ? `${withSearch}#${hash}` : withSearch;
}
